import { TUNING } from '~/config/tuning'
// Explicit import: Nuxt's auto-import scan silently skipped this export
// (v0.76–v0.78 act-boss freeze) — don't trust the scanner for it.
import { firstClearStardust, stardustForAct } from '~/composables/useStardust'
import { useStarweave } from '~/composables/useStarweave'
import { ascensionActHealPct, ascensionDraftSize, ascensionMaxHpPenalty, ascensionSecretBoss, ascensionStartingGold, ascensionStartsInjured, GAUNTLET_LEVEL } from '~/data/ascensions'
import { appendGauntletAct, makeSecretBossNode } from '~/utils/mapgen'
import { applyBossSkill, BOSS_SKILL_IDS, rollBossSkill } from '~/data/bossSkills'
import { getCreatureDef, rollDraftOptions, CREATURE_POOL, HOMAGE_AWARDS } from '~/data/creatures'
import { FIRST_DISPATCH } from '~/data/firstDispatch'
import { funnelCampaign, funnelPractice, track } from '~/utils/funnel'
import { FIRST_STEPS_CLOUD_STATE, readFirstSteps, readLocalPlay, startsFirstRun, writeFirstSteps, type CloudPlay } from '~/utils/firstSteps'
import { emptyFirstRunTally, normalizeFirstRunTally } from '~/utils/firstRunRails'
import { COACH, coachStart } from '~/utils/firstRunGuide'
import { makeCreature } from '~/utils/roster'
import { canLearnSkill, canUseCreatureSkill, creatureOwnSkill, evergrowthAbsorb, gildedRestockMult, gildedScalesDiscount, grantXp, greenThumbArrive, toggleCreatureSkill, useBloom, useBulwark, canDecree, useDeluge, useForesight, useGraft, useMimic, usePlunder, useRefract, useReforge, useTithe, useBalanceOfAges } from '~/utils/battle'
import { hasWeeklyMod, SUDDEN_DEATH_HP, weeklyModFor } from '~/data/weeklyMods'
import { FIELD_HEIGHT } from '~/composables/useEncounter'
import { FOREST_THEMES, forestById, isBossRoomId } from '~/data/forests'
import { rollEvent } from '~/data/events'
import { rand, setRandomSource, shuffle } from '~/utils/random'
import { TRAINED_SKILL_IDS, skillsUnlockedAt } from '~/data/trainedSkills'
import { dailyAscension, seededRng, weeklyDateKey, weeklySeed, withRunStream } from '~/utils/seededRandom'
import { rarityOf } from '~/data/rarity'
import { RELIC_POOL, relicAvailable } from '~/data/relics'
import { KEEPSAKE_RELIC, KEEPSAKE_SKILL } from '~/data/keepsakes'
import { ownedSynergyIds, synergyWeightMult } from '~/data/synergies'
import { getRewardDef, REWARD_DEFS } from '~/data/rewards'
import { ELEMENTS, type Creature, type CreatureDef, type MapNode, type RunState, type Stats } from '~/types/game'
import { hasStarweave } from '~/utils/starweaveRun'
import { normalizeStarweave } from '~/utils/normalizeStarweave'
import { runBindsStarweave, STARWEAVE_UNLOCK_ASCENSION } from '~/data/starweave'
import {
  initializeStarweaveRun, starweaveDraftExtra, starweaveRecruitCatchUp,
  starweaveRations, starweaveShopCashback, canStarweaveRelicReroll,
  rerollStarweaveRelics, canStarweaveSkillReroll, rerollStarweaveTraining,
  offerStarweaveCollector, consumeStarweaveCollector, canStarweaveDoubleTraining,
} from '~/utils/starweaveRewards'

// v6: free-form field map — missions carry positions, roads laid at dispatch.
export const RUN_STORAGE_KEY = 'elemental-dispatch-run-v6'
/** 📡 v6.83: `error.name` of the New Run failure the title can offer a way out of. */
export const STARWEAVE_UNAVAILABLE = 'StarweaveUnavailable'
// 🎲 v5.54: the daily's OWN slot — never collides with the campaign.
export const DAILY_STORAGE_KEY = 'elemental-dispatch-daily-v6'
export const PRACTICE_STASH_KEY = 'elemental-dispatch-practice-stash-v1'

export function useRunState() {
  return useState<RunState | null>('run', () => null)
}

/** 🏦 v4.92: run ids key the wallet's once-only act payouts. */
function newRunId(): string {
  try { return crypto.randomUUID() } catch { return `r-${Date.now()}-${Math.floor(Math.random() * 1e6)}` }
}

type PracticeEncounter = NonNullable<RunState['encounter']>

/** 🎓 v7.38: the lesson tag and tutorialMissionId arrived in v7.20, so a
 * v7.04-v7.19 practice save carries its live lesson UNTAGGED - and the
 * derail sweep took it for a stray. Know it by the beat it was dealt from
 * (the same asks, seats and fuse) and tag it, so the script judges it. */
function adoptUntaggedLesson(enc: PracticeEncounter) {
  if (enc.tutorialMissionId || enc.missions.some(m => m.tutorialLesson)) return
  const beat = FIRST_DISPATCH.script[(enc.tutorialStep ?? 0) - 1]
  if (!beat) return
  const lesson = enc.missions.find(m =>
    ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status)
    && m.slots === beat.slots && m.expireTotal === beat.fuse
    && ELEMENTS.every(el => (m.required?.[el] ?? 0) === (beat.required[el] ?? 0)))
  if (!lesson) return
  lesson.tutorialLesson = beat.lesson
  enc.tutorialMissionId = lesson.id
}

/** 🎓 v7.38: the practice wins the script can vouch for. A taught lesson
 * ('dispatch', 'surge', 'pair', 'skill') advances only on a win, so every one the step has
 * moved past was won once; the lesson still being judged counts if its
 * verdict is in. 'expire' never counts (useEncounter's practiceAside). */
function taughtWins(enc: PracticeEncounter): number {
  const script = FIRST_DISPATCH.script
  const step = enc.tutorialStep ?? 0
  const passed = enc.tutorialMissionId ? step - 1 : step
  let wins = script.slice(0, Math.max(0, passed)).filter(b => b.lesson !== 'expire').length
  const judged = enc.tutorialMissionId ? script[step - 1] : undefined
  if (judged && judged.lesson !== 'expire' && enc.tutorialOutcome === 'won') wins++
  return wins
}

/** 🎓 v7.41 (review): what a first run's "Try again" keeps from the run that
 * fell - the practice's marks ('guide:active', 'mission', 'done') and the
 * classic tip cards a first location can show. Never the first-run guide's
 * lesson marks (they replay: Try again is guided) nor a later card (boss,
 * training...), so a tip card's "Skip tutorial" (it marks every card)
 * leaves the retry's later tips on. 🎓 v7.41 (integration review): but the
 * menu's "Stop guidance" (COACH.off) IS kept - it is the player's choice,
 * not a lesson, and a retry must not bring back the guide they turned off. */
const RETRY_KEEPS_TIPS: readonly string[] = ['guide:active', 'draftIntro', 'map', 'encounter', 'banner', 'mission', 'done', 'gold', 'levelup', 'failure', 'enrage', COACH.off]

/** Fills in fields that older saved runs may not have yet.
 *
 * @param maxAscension when given (the account's unlocked ceiling), the
 * run's ascension is CLAMPED to it — a hand-edited save claiming A10
 * must not collect 4× payouts and an unearned first-clear (audit E8).
 * The wallet server clamps again, but the run itself shouldn't lie. */
export function normalizeRun(raw: Partial<RunState>, maxAscension?: number): RunState {
  const state: RunState = {
    phase: 'draft',
    startedAt: Date.now(),
    playtimeMs: 0,
    bossIntroSeen: false,
    gold: 0,
    actLog: {},
    actSummary: null,
    shopStock: null,
    plannedPath: [],
    tutorial: false,
    tutorialSeen: [],
    ascension: 0,
    act: 1,
    hp: TUNING.player.startingHp,
    maxHp: TUNING.player.startingHp,
    roster: [],
    maxRosterSize: TUNING.draft.startingMaxRosterSize,
    bonusId: null,
    relics: [],
    draft: null,
    map: null,
    position: null,
    activeNodeId: null,
    completedNodeIds: [],
    bossDefeated: false,
    defeated: false,
    encounter: null,
    pendingRewards: null,
    pendingRewardPools: [],
    rewardResolution: null,
    ...raw,
  }
  // Recruit options changed shape (strings → pre-rolled objects); drop a
  // stale mid-recruit pick from older saves rather than crash on it.
  if (
    state.rewardResolution?.type === 'recruit'
    && typeof (state.rewardResolution.options[0] as unknown) === 'string'
  ) {
    state.rewardResolution = null
  }
  // Ascensions (v0.60): older saves are Normal runs.
  if (typeof state.ascension !== 'number') state.ascension = 0
  if (typeof maxAscension === 'number' && Number.isFinite(maxAscension)) {
    state.ascension = Math.min(state.ascension, Math.max(0, Math.floor(maxAscension)))
  }
  // 🏦 v4.92: pre-ledger saves get their wallet key here.
  if (typeof state.runId !== 'string' || state.runId.length < 4) state.runId = newRunId()
  // Training missions (v0.79): per-act once-only tracker.
  if (!Array.isArray(state.trainingActs)) state.trainingActs = []
  // 🎭 v7.56: the face-off scenes played - absent until the first one plays.
  if (state.storyScenesSeen !== undefined && !Array.isArray(state.storyScenesSeen)) delete state.storyScenesSeen
  // 🔁 Offer decline-cycling (v5.17): pre-cycle saves start a fresh bag.
  const cyc = state.declinedOffers as { relic?: unknown; recruit?: unknown; skill?: unknown } | undefined
  if (!cyc || !Array.isArray(cyc.relic) || !Array.isArray(cyc.recruit) || !Array.isArray(cyc.skill))
    state.declinedOffers = { relic: [], recruit: [], skill: [] }
  // ❓ Mystery events (v5.39).
  if (!Array.isArray(state.seenEvents)) state.seenEvents = []
  if (state.pendingEvent === undefined) state.pendingEvent = null
  // 🤫 A10 secret boss (v0.84).
  if (typeof state.secretBossRevealed !== 'boolean') state.secretBossRevealed = false
  // ⚔️ Gauntlet mode (v2.34).
  if (typeof state.gauntlet !== 'boolean') state.gauntlet = false
  // 🌟 Rare-creature snapshot (v1.01).
  if (!Array.isArray(state.unlockedCreatures)) state.unlockedCreatures = []
  // 🎓 v7.38 (per Javi, F39): DRAFT - the in-progress picks ride the run so a
  // reload keeps them. Older saves start empty; an id no longer pickable (not
  // on offer, not owned) drops out, the same rule confirmDraft applies.
  if (state.draft) {
    const pickable = new Set([...(state.draft.options ?? []), ...state.unlockedCreatures])
    state.draft.selected = Array.isArray(state.draft.selected)
      ? [...new Set(state.draft.selected.filter(id => typeof id === 'string' && pickable.has(id)))]
      : []
  }
  // 👑 Center-ring bosses (v1.45): boss missions persist their spawn spot,
  // so fights spawned before v1.43 still stand at the old top-gate
  // position — snap the ROOT boss (never splits/minions) onto the ring.
  if (state.encounter && isBossRoomId(state.encounter.forestId)) {
    const room = forestById(state.encounter.forestId)
    if (room?.bossSpot) {
      for (const mission of state.encounter.missions) {
        if (mission.tier === 'boss' && !(mission.fractureDepth ?? 0) && !mission.minion) {
          mission.x = room.bossSpot.x
          mission.y = Math.round((room.bossSpot.y / 100) * FIELD_HEIGHT)
            // Yin & Yang stands a step higher (v2.30) — see spawnMission.
            - (mission.bossSkill === 'yinyang' ? 22 : 0)
        }
      }
    }
  }
  // 🗺️ Whole-canvas fields (v2.62): the extended paintings now ship
  // uncropped, so every site sits further down its canvas than it did in
  // the save. Missions persist their spawn spot — re-snap them onto their
  // poi, or a run reloaded mid-node leaves its markers stranded in the
  // wrong stretch of terrain.
  const field = state.encounter ? forestById(state.encounter.forestId) : null
  if (state.encounter && field && !field.bossSpot) {
    for (const mission of state.encounter.missions) {
      const poi = mission.poi !== undefined ? field.pois[mission.poi] : undefined
      if (!poi) continue
      mission.x = poi.x
      mission.y = (poi.y / 100) * FIELD_HEIGHT
    }
  }
  // 🗺️ Biome per act (v1.39): draw acts.count themes without replacement
  // so no biome repeats within a run. Older saves roll theirs here.
  // v5.54: routed through rand() so a daily's biomes are seeded too.
  if (!Array.isArray(state.actThemes) || state.actThemes.length < TUNING.acts.count) {
    const pool = [...FOREST_THEMES]
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [pool[i], pool[j]] = [pool[j]!, pool[i]!]
    }
    state.actThemes = pool.slice(0, TUNING.acts.count)
  }
  // With injuries inactive for this run, clear lingering flags; otherwise
  // sync the stack counter (legacy saves carry only the boolean).
  for (const creature of state.roster) {
    if (!injuriesActive(state)) healInjuriesFully(creature)
    else if (typeof creature.injuries !== 'number') creature.injuries = creature.injured ? 1 : 0
  }
  // Focus Bell changed from once-per-encounter to a 3-use counter.
  if (state.encounter && typeof state.encounter.focusBellUses !== 'number') {
    state.encounter.focusBellUses
      = (state.encounter as unknown as { focusBellUsed?: boolean }).focusBellUsed ? 3 : 0
  }
  if (state.encounter) {
    if (typeof state.encounter.fastDeployUsed !== 'boolean') state.encounter.fastDeployUsed = false
    if (typeof state.encounter.teamJournalPrimed !== 'boolean') state.encounter.teamJournalPrimed = false
  }
  // Relics removed from the pool (e.g. Delay Charm) vanish from old saves.
  state.relics = state.relics.filter(id => RELIC_POOL.some(r => r.id === id))
  // Reward offers whose def was removed (e.g. Campfire Meal) drop away.
  if (state.pendingRewards) {
    state.pendingRewards = state.pendingRewards.filter(offer =>
      offer.pool === 'relic'
        ? RELIC_POOL.some(r => r.id === offer.defId)
        : REWARD_DEFS.some(d => d.id === offer.defId),
    )
    if (state.pendingRewards.length === 0) state.pendingRewards = null
  }
  // Run clock rebased to active play time (v1.12): older saves only
  // carry startedAt — seed from wall time once, then it accrues only
  // while the tab is visible.
  if (typeof raw.playtimeMs !== 'number') {
    state.playtimeMs = Math.max(0, Date.now() - state.startedAt)
  }
  // Gold/shop/summary/planning (v0.35): defaults for older saves.
  if (typeof state.gold !== 'number') state.gold = 0
  if (!state.actLog || typeof state.actLog !== 'object') state.actLog = {}
  if (state.actSummary === undefined) state.actSummary = null
  if (state.shopStock === undefined) state.shopStock = null
  if (!Array.isArray(state.plannedPath)) state.plannedPath = []
  if (typeof state.tutorial !== 'boolean') state.tutorial = false
  if (!Array.isArray(state.tutorialSeen)) state.tutorialSeen = []
  // 🎓 v7.40 (per Javi, §3 D): older saves were never first runs, and only
  // a campaign run can be one (never the weekly, the practice, the Gauntlet
  // or a Puzzle Game session).
  if (typeof state.firstRun !== 'boolean' || state.mode || state.gauntlet || typeof state.puzzleLevel === 'number') state.firstRun = false
  // 🎓 v7.41 (per Javi, §3 C + D): a first run's tally, cleaned. It outlives
  // `firstRun` (the rails cover row 1 too); a v7.40 first run gets an empty
  // one; no other run carries one (old saves, the weekly, the practice...).
  const tally = state.mode || state.gauntlet || typeof state.puzzleLevel === 'number' ? undefined : normalizeFirstRunTally(state.firstRunTally)
  if (tally) state.firstRunTally = tally
  else if (state.firstRun) state.firstRunTally = emptyFirstRunTally()
  else delete state.firstRunTally
  if (state.encounter) {
    // v0.88 (the Yin & Yang bug): the boss/puzzle "no gold here" marker
    // used to be Infinity, which JSON saves as null — this migration
    // then re-armed the clock on a reloaded BOSS fight, and the gold
    // spawner cloned the boss itself with a 💰 tag, blocking completion
    // forever. The marker is a deliberate null now; only re-arm on
    // fields that actually host gold, and heal any stray clones.
    const activeType = state.map?.nodes.find(n => n.id === state.activeNodeId)?.type
    // 🎓 v7.38 (F04, per Javi): the practice's null clocks are deliberate
    // too - the script deals every beat (useEncounter's tickFirstDispatch).
    // A reload or a "Continue" re-armed them to 5 / 20, random missions
    // crowded the field and the next lesson never came.
    const practice = state.mode === 'tutorial'
    const goldField = activeType !== 'boss' && activeType !== 'puzzle' && !practice
    if (typeof state.encounter.nextGoldSpawnIn !== 'number') {
      state.encounter.nextGoldSpawnIn = goldField ? 20 : null
    }
    if (!goldField) state.encounter.nextGoldSpawnIn = null
    // v1.12 (the puzzle-node twin of the same bug): the REGULAR mission
    // clock also used to be Infinity on puzzle fields, so a reload
    // re-armed it and grew normal missions among the trials. Pin it to
    // null there and sweep any strays that already spawned.
    // 🎓 v7.38: the practice pins it the same way - and sweeps every
    // mission that is not a lesson, so an already-derailed save heals.
    // A v7.04-v7.19 save never tagged its lesson: adopt it first, or the
    // sweep took the real lesson and the script skipped ahead.
    const practiceLive = practice && state.encounter.status === 'active'
    if (practiceLive) adoptUntaggedLesson(state.encounter)
    const derailed = practiceLive
      && (typeof state.encounter.nextSpawnIn === 'number'
        || state.encounter.missions.some(m => !m.tutorialLesson)
        || state.encounter.enrageAt !== undefined)
    if (activeType === 'puzzle' || practice) state.encounter.nextSpawnIn = null
    else if (typeof state.encounter.nextSpawnIn !== 'number') state.encounter.nextSpawnIn = 5
    const before = state.encounter.missions
    state.encounter.missions = before.filter(m =>
      !(m.goldReward && m.tier === 'boss')
      && !(activeType === 'puzzle' && !m.puzzle)
      && !(derailed && !m.tutorialLesson))
    if (state.encounter.missions.length !== before.length) {
      // A hybrid may have held creatures hostage — free any orphans.
      // 🎓 v7.38: a team already walking home keeps its trip - freeing it
      // here let the trip's arrival later clobber a fresh assignment.
      const liveIds = new Set(state.encounter.missions.flatMap(m => m.assignedCreatureIds))
      const walking = new Set(state.encounter.returnTrips.flatMap(t => t.creatureIds))
      for (const creature of state.roster) {
        if (creature.assignedMissionId && !liveIds.has(creature.id) && !walking.has(creature.id)) {
          creature.available = true
          creature.assignedMissionId = null
        }
      }
    }
    if (derailed) {
      const enc = state.encounter
      // The lesson being judged keeps its verdict unless it was swept.
      if (enc.tutorialMissionId && !enc.missions.some(m => m.id === enc.tutorialMissionId)
        && before.some(m => m.id === enc.tutorialMissionId)) {
        enc.tutorialMissionId = undefined
        enc.tutorialOutcome = undefined
      }
      // The random arrivals walked the counter toward the enrage: count
      // the lessons dealt again, and disarm it.
      enc.spawnCount = Math.max(0, enc.tutorialStep ?? 0)
      delete enc.enrageAt
    }
    // 🎓 v7.38: the goal counts taught wins only - a random win (or a
    // pre-v7.20 win on the expiry lesson) ended the practice before the
    // expiry, surge, pair and skill lessons.
    if (practiceLive) state.encounter.successes = Math.min(state.encounter.successes, taughtWins(state.encounter))
  }
  // Boss skills (v0.42): older maps pre-date them — roll one per boss,
  // keeping the final boss's historical Knockout. Skills are UNIQUE per
  // run (v0.48), so migrated bosses draw from the unused pool.
  if (state.map) {
    const bossNodes = state.map.nodes.filter(n => n.type === 'boss')
    const finalRow = bossNodes.length ? Math.max(...bossNodes.map(n => n.row)) : -1
    const used = new Set(bossNodes.map(n => n.preview?.boss?.skill).filter(Boolean))
    for (const node of bossNodes) {
      if (node.preview?.boss && !node.preview.boss.skill) {
        const wanted = node.row === finalRow && !used.has('knockout') ? 'knockout' : undefined
        const pool = BOSS_SKILL_IDS.filter(id => !used.has(id))
        const skill = wanted ?? pool[Math.floor(Math.random() * pool.length)] ?? rollBossSkill()
        applyBossSkill(node.preview.boss, skill)
        used.add(skill)
      }
    }
  }
  for (const creature of state.roster) {
    // Surge skills (v0.30): default fields for older saves.
    if (typeof creature.surgePrimed !== 'boolean') creature.surgePrimed = false
    if (typeof creature.surgeCooldown !== 'number') creature.surgeCooldown = 0
    // Echo Training merged into Rallying Cry (v1.12): migrate the
    // learned id so old holders keep the (now stronger) skill.
    if ((creature.learnedSkills ?? []).includes('echo-training')) {
      creature.learnedSkills = [...new Set(
        creature.learnedSkills!.map(id => (id === 'echo-training' ? 'rallying-cry' : id)),
      )]
    }
    // Speed baseline rescale (v0.30, 1/2/3 → 2/3/4): lift old saves to
    // at least the creature's current innate speed. ✏️ v7.80: the name
    // follows the definition too - a renamed creature (per Javi) reads its
    // new name in a run already under way (the roster copied the old one).
    try {
      const def = getCreatureDef(creature.defId)
      creature.speed = Math.max(creature.speed, def.speed)
      creature.name = def.name
    } catch { /* unknown defId: keep stored speed and name */ }
  }
  normalizeStarweave(state)
  return state
}

// v7.04: makeCreature lives in utils/roster.ts (the encounter mints one too).

export function useRun() {
  const run = useRunState()
  // 📊 v5.64: the private lifetime ledger — settled at run start,
  // victory, defeat and abandon (never for Puzzle Game sessions).
  const lifetime = useLifetimeStats()

  /** A replaced/nulled run that never reached an ending counts as
   * ABANDONED — your next New Run confesses for you. */
  function settleAsAbandoned(prev: RunState | null) {
    if (prev?.mode === 'tutorial') return // 🎓 v7.04: the First Dispatch never counts
    if (!prev || prev.defeated || prev.bossDefeated || typeof prev.puzzleLevel === 'number') return
    lifetime.recordRunEnd(statsModeOf(prev), 'abandoned', prev.playtimeMs ?? 0, prev.ascension ?? 0, { act: prev.act })
  }

  async function startNewRun(withTutorial = false, ascension = 0, gauntlet = false, daily = false, withoutStarweave = false) {
    // Signed ownership is fetched before the run starts, with a same-account
    // confirmed cache available offline. Seeded expeditions never read it.
    let starweaveOwned: string[] = []
    // 🔓 v6.85 (per Javi): the Starweave is an Ascension 5 unlock. Below it a
    // run sets out without stars and never waits on the account - unless this
    // account already bound stars before the gate existed (their Stardust and
    // talents stay honoured until they unbind).
    const ascensionGate = useAscension()
    ascensionGate.load()
    const starweaveLocked = ascensionGate.unlocked.value < STARWEAVE_UNLOCK_ASCENSION
      && useStarweave().owned.value.length === 0
    if (!daily && !withoutStarweave && !starweaveLocked) {
      const talents = useStarweave()
      const refreshed = await talents.load()
      if (!refreshed && !talents.loaded.value) {
        // 📡 v6.83: offline (or a 503/429/401) with no same-account cache used
        // to be a dead end - New Run simply failed. The title now catches
        // this by name and offers Retry or a run without stars.
        const error = new Error('Your Starweave could not be reached.')
        error.name = STARWEAVE_UNAVAILABLE
        throw error
      }
      starweaveOwned = [...talents.owned.value]
    }
    // 📊 Starting over the SAME lane's unfinished run abandons it (the
    // confirm popup's "Abandon & start new", a weekly retry). A daily
    // start over the campaign leaves the campaign saved — not counted.
    const prev = run.value
    const nextMode = daily ? ('weekly' as const) : gauntlet ? ('gauntlet' as const) : ('campaign' as const)
    // 🎓 v7.40 (per Javi, §3 D): a FIRST RUN - read before this start settles
    // anything (utils/firstSteps.ts: A0 campaign, a restart of one, or a
    // device and account with no earlier play).
    const store = typeof localStorage === 'undefined' ? null : localStorage
    const firstRun = startsFirstRun({ campaign: !daily && !gauntlet, ascension }, prev,
      readFirstSteps(store), readLocalPlay(store), useState<CloudPlay | null>(FIRST_STEPS_CLOUD_STATE, () => null).value)
    if (prev && statsModeOf(prev) === nextMode) settleAsAbandoned(prev)
    // 🎲 DAILY (v5.54, designed with Javi): one seed per UTC day deals
    // the same map, draft and ascension (A2–A9) to every player. The
    // seeded source covers everything rolled inside this function; play
    // itself rolls live dice again the moment we leave the try.
    const seed = daily ? weeklySeed() : null
    if (seed) setRandomSource(seededRng(seed))
    try {
      const fresh = normalizeRun({ draft: { pickIndex: 0, options: [], bonusOptions: [] } })
      fresh.tutorial = withTutorial
      fresh.firstRun = firstRun
      // 🎓 v7.41: ...and it keeps a tally for its rails and its kind defeat.
      if (firstRun) {
        fresh.firstRunTally = emptyFirstRunTally()
        coachStart(fresh, useOnboarding().preference.value)
      }
      fresh.ascension = daily ? dailyAscension(seed!) : ascension
      // ⚔️ Gauntlet (v2.34): A10 rules, but no summit — see completeActiveNode.
      fresh.gauntlet = gauntlet
      if (daily) {
        fresh.mode = 'daily'
        fresh.seed = seed!
        // Field name kept for save compatibility — it holds the WEEK key
        // (the Monday) since v5.63.
        fresh.dailyDate = weeklyDateKey()
        // 🗓️⚗️ v5.66: freeze this week's LAW into the run — a schedule
        // edit mid-week never mutates an in-flight expedition.
        const law = weeklyModFor(fresh.dailyDate)
        if (law) fresh.weeklyMod = law.id
      }
      // Ascension HP tax (−5 at each of A2/A4/A6/A8).
      fresh.maxHp = Math.max(5, TUNING.player.startingHp - ascensionMaxHpPenalty(fresh.ascension))
      fresh.hp = fresh.maxHp
      // 💀 Sudden Death week: ten hearts, and nothing hits for more
      // than one (see suddenDeathClamp).
      if (hasWeeklyMod(fresh, 'sudden')) {
        fresh.maxHp = SUDDEN_DEATH_HP
        fresh.hp = SUDDEN_DEATH_HP
      }
      if (!daily) initializeStarweaveRun(fresh, starweaveOwned)
      // 💰 v4.13 (per Javi): a starting purse — 45, cut to 20 from A5 up.
      fresh.gold = ascensionStartingGold(fresh.ascension, TUNING.player.startingGold)
      // The full route exists up front so the player can plan their draft.
      fresh.map = generateMap(fresh.ascension, fresh.weeklyMod ?? null)
      // Draft v1.01 (per Javi, supersedes v0.61's whole-pool free pick):
      // random options rolled rarity-weighted — plus the player's
      // PURCHASED creatures, always pickable from their own section.
      // v2.75: the roll is biased toward what they do NOT own.
      // 🎲 Daily: NO personal collection — the seeded roll from the full
      // catalog is the whole offer, identical for everyone.
      if (daily) {
        fresh.unlockedCreatures = []
      } else {
        const ownedRares = useOwnedCreatures()
        ownedRares.load()
        fresh.unlockedCreatures = [...ownedRares.owned.value]
        // 🦕 v5.95: an awakened Legacy joins the permanent roster
        // lane (never the random rolls; seeded modes stay bare).
        const ach = useAchievements()
        ach.load()
        if (ach.has('legacy-awakens') && !fresh.unlockedCreatures.includes('legacy')) {
          fresh.unlockedCreatures.push('legacy')
        }
        // 🎩 v6.88: the homage creatures follow the same lane - each chain
        // end awakens its own (ids without a kit yet are skipped).
        for (const [achievement, ids] of HOMAGE_AWARDS) {
          if (!ach.has(achievement)) continue
          for (const id of ids) if (!fresh.unlockedCreatures.includes(id) && CREATURE_POOL.some(c => c.id === id)) fresh.unlockedCreatures.push(id)
        }
        // 🎗️ v6.54: the keepsakes the ledger has unlocked ride this run
        // (their one offer each is pinned in rewards; seeded modes never).
        // 🎗️ v7.48 (per Javi): the campaign only - a gauntlet run carries neither.
        fresh.keepsakes = { relic: !gauntlet && ach.has('keepsake-relic'), skill: !gauntlet && ach.has('keepsake-lesson') }
      }
      // 🎒 Loadout Lock week deals EIGHT (and keeps five — see
      // draftRosterSize); no recruits all week.
      // 🎯 v7.33 (per Javi): the pool covers the starting row - one card
      // built for each first node's posted pair.
      fresh.draft!.options = rollDraftOptions(
        hasWeeklyMod(fresh, 'loadout') ? 8 : TUNING.draft.poolSize + (hasStarweave(fresh, 'attunement-spark') ? 1 : 0),
        fresh.unlockedCreatures,
        fresh.map.nodes.filter(n => n.row === 0).map(n => n.preview?.focusElements ?? []),
      )
      // 👁️ v6.03 (per Javi's 3-state codex): an OFFER is a sighting.
      for (const id of fresh.draft!.options) useCollection().record('creatures', id, { announce: false })
      // 🎓 v5.56 (per Javi): the Training Grounds curriculum is
      // skeleton too — a seed-shuffled deck every teaching surface
      // deals from in order (see rollTrainingOptions). Rolled LAST so
      // the day's map/draft stay identical to what pre-deck clients
      // already dealt from the same seed.
      if (daily) fresh.trainingDeck = shuffle(TRAINED_SKILL_IDS)
      // 🎓 v5.86 (Javi's ladder): campaign + gauntlet curricula only
      // teach what the account's ascension progress has unlocked —
      // frozen into the run like the weekly law, so an unlock mid-run
      // never mutates an in-flight curriculum. The weekly keeps the
      // FULL set (shared seed: same run for everyone).
      if (!daily) {
        const asc = useAscension()
        asc.load()
        fresh.skillPool = skillsUnlockedAt(asc.unlocked.value)
      }
      run.value = fresh
      // 🎓 v7.40: the device's first run is open until location 1 falls.
      if (firstRun) writeFirstSteps(store, { firstRun: 'open' })
      lifetime.recordRunStart(nextMode)
    } finally {
      if (seed) setRandomSource(null)
    }
  }

  /** 🎲 v5.54: start today's daily — seeded everything, own slot. */
  function startDailyRun() {
    return startNewRun(false, 0, false, true)
  }

  /** 🎓 v7.04 (per Javi): a first-timer's guided FIRST DISPATCH - one
   * creature, one scripted node, no draft; a throwaway run in its own mode
   * (data/firstDispatch.ts). Never counted, synced or boarded. */
  function startFirstDispatch(options: { preserveRun?: boolean; skipWelcome?: boolean; replay?: boolean } = {}) {
    const prev = run.value
    // 🎓 v7.40 (per Javi): How to play offers the practice only with no
    // campaign run in progress; a live weekly is stashed (preserveRun) and
    // comes back when the practice ends.
    const stash = !!options.preserveRun && !!prev && prev.mode !== 'tutorial'
    if (stash) {
      // Write the backup before touching the active run. A storage failure
      // leaves the real run intact and the caller can show the error.
      localStorage.setItem(PRACTICE_STASH_KEY, JSON.stringify({
        run: prev,
        campaignRaw: localStorage.getItem(RUN_STORAGE_KEY),
        preference: useOnboarding().preference.value,
        account: practiceAccount(),
      }))
    } else if (prev) settleAsAbandoned(prev)
    const fresh = normalizeRun({})
    fresh.mode = 'tutorial'
    fresh.tutorial = true
    if (options.skipWelcome) fresh.tutorialSeen.push('fdWelcome')
    if (stash) fresh.tutorialSeen.push('guide:stash')
    // 🎓 v7.40 (review): a REPLAY is a player with a run of their own (the
    // stash) or one who finished the practice before; a first-timer's first
    // practice from How to play counts in the funnel like any other.
    if (stash || (options.replay && tutorialAlreadyDone())) fresh.tutorialSeen.push('guide:replay')
    if (tutorialAlreadyDone()) fresh.tutorialSeen.push('guide:reward-claimed')
    fresh.phase = 'encounter'
    fresh.gold = TUNING.player.startingGold
    fresh.roster = [makeCreature(getCreatureDef(FIRST_DISPATCH.hero))]
    fresh.actThemes = ['forest']
    fresh.map = {
      rows: 1,
      rowsPerAct: 1,
      nodes: [{ id: FIRST_DISPATCH.nodeId, row: 0, col: 0, type: 'swarm', next: [], preview: { requiredSuccesses: 4, focusElements: ['fire', 'water'], tier: 'easy' } }],
    }
    fresh.position = FIRST_DISPATCH.nodeId
    fresh.activeNodeId = FIRST_DISPATCH.nodeId
    run.value = fresh
    // 🎓 v7.40 (review): a replay leaves the device's onboarding choice alone.
    if (!fresh.tutorialSeen.includes('guide:replay')) useOnboarding().setPreference('learning')
    // 🎓 v7.39 (per Javi): the funnel's practice opens here (a replay, or a
    // re-tick once it was done - 'guide:reward-claimed' above - never counts).
    if (funnelPractice(fresh)) track('practice_start', { once: true })
  }

  function restorePracticeRun(): boolean {
    // 🎓 v7.40: only a practice that stashed a run restores one - a replay
    // started with no run in progress ('guide:replay' alone) ends like a
    // first practice, and a stash some other session left behind is never
    // mistaken for this one's.
    if (run.value?.mode !== 'tutorial' || !run.value.tutorialSeen.includes('guide:stash')) return false
    const raw = localStorage.getItem(PRACTICE_STASH_KEY)
    if (!raw) throw new Error(useLocale().t('onboarding.restoreError'))
    const saved = JSON.parse(raw) as { run: RunState; campaignRaw: string | null; preference: string; account?: string | null }
    if ((saved.account ?? null) !== practiceAccount()) throw new Error(useLocale().t('onboarding.restoreAccountError'))
    if (!saved.run || saved.run.mode === 'tutorial') throw new Error(useLocale().t('onboarding.restoreError'))
    const restored = normalizeRun(saved.run)
    // A weekly replay temporarily occupied the campaign storage slot.
    if (restored.mode === 'daily') {
      if (saved.campaignRaw) localStorage.setItem(RUN_STORAGE_KEY, saved.campaignRaw)
      else localStorage.removeItem(RUN_STORAGE_KEY)
    }
    run.value = restored
    const preference = saved.preference === 'dismissed' || saved.preference === 'learning' || saved.preference === 'complete' ? saved.preference : 'complete'
    useOnboarding().setPreference(preference)
    localStorage.removeItem(PRACTICE_STASH_KEY)
    return true
  }

  function practiceAccount(): string | null {
    const user = useAuthUser().value
    if (user && !user.anonymous) return user.uid
    // Auth hydrates asynchronously on reload. The agreed identity keeps
    // a signed-in replay bound to its owner while Firebase reconnects.
    try {
      const identity = JSON.parse(localStorage.getItem('elemental-dispatch-run-identity') ?? 'null')
      return identity?.anonymous === false && typeof identity.uid === 'string' ? identity.uid : null
    } catch { return null }
  }

  /** The First Dispatch ends into a real run at the title's chosen rung: the
   * welcome Stardust is queued (a skipped run earns nothing), the taught tips
   * stay quiet, and the draft opens. */
  async function finishFirstDispatch(skipped = false) {
    if (run.value?.mode !== 'tutorial') return
    // 🎓 v7.39 (per Javi): the funnel counts the ending before the real run is
    // dealt, so a failed deal (offline Starweave) still shows it.
    if (funnelPractice(run.value)) track(skipped ? 'practice_skip' : 'practice_done', { once: true })
    const rewardEligible = !skipped && !run.value.tutorialSeen.includes('guide:reward-claimed')
    if (run.value.tutorialSeen.includes('guide:replay')) {
      // Validate ownership and restore before submitting any account reward.
      if (restorePracticeRun()) {
        if (rewardEligible) useStardust().earnEvent({ kind: 'tutorial-first' })
        return
      }
    }
    if (!skipped && !run.value.tutorialSeen.includes('guide:reward-claimed')) {
      useStardust().earnEvent({ kind: 'tutorial-first' })
      run.value.tutorialSeen.push('guide:reward-claimed')
    }
    if (!run.value.tutorialSeen.includes('guide:replay')) useOnboarding().setPreference(skipped ? 'dismissed' : 'learning')
    const asc = useAscension()
    asc.load()
    const level = Math.min(asc.choice.value, asc.unlocked.value)
    const gauntlet = level >= GAUNTLET_LEVEL
    // 🎓 v7.37 (per Javi, restoring his v7.04 rule): a skip only skips the
    // PRACTICE - the real run keeps its tips either way.
    try {
      await startNewRun(true, gauntlet ? 10 : level, gauntlet, false, false)
    } catch (error) {
      if (!(error instanceof Error && error.name === STARWEAVE_UNAVAILABLE)) throw error
      await startNewRun(true, gauntlet ? 10 : level, gauntlet, false, true)
    }
    const state = run.value as RunState | null
    // 🎓 v7.20: 'encounter' stays live - its controls line (camp, fast-forward,
    // discard) was never practised; the odds rule ('mission') was. 🎓 v8.02:
    // so was Surge - the first run's "Tap Surge" one-liner ('coach:surge') stays quiet.
    if (state && !skipped) for (const id of ['guide:active', 'mission', 'done', 'coach:surge']) if (!state.tutorialSeen.includes(id)) state.tutorialSeen.push(id)
  }

  /** 🎓 v7.41 (per Javi, §3 C - F14): the kind defeat's "Try again". A first
   * run that fell starts over AS a first run (startNewRun's carry rule,
   * utils/firstSteps.ts carriesFirstRun): Ascension 0, tips on, the rails and
   * a fresh tally, and the draft next. What the practice taught stays taught
   * (the ids finishFirstDispatch pre-seen), and so do the classic tip cards
   * the fallen run already showed (RETRY_KEEPS_TIPS) - "Your first fight"
   * is not true twice. The first-run guide's lesson marks are not carried, so
   * the guided run is guided again - unless the player stopped it (COACH.off
   * rides along). The fallen run already ended and
   * was recorded where it fell (damagePlayer: a loss, the funnel's
   * first_defeat), so nothing here settles it again. False (nothing done)
   * unless the run on screen is a fallen first run. */
  async function retryFirstRun(): Promise<boolean> {
    const prev = run.value
    if (!prev?.defeated || prev.firstRun !== true) return false
    const taught = prev.tutorialSeen.filter(id => RETRY_KEEPS_TIPS.includes(id))
    try {
      await startNewRun(true, 0, false, false, false)
    } catch (error) {
      if (!(error instanceof Error && error.name === STARWEAVE_UNAVAILABLE)) throw error
      await startNewRun(true, 0, false, false, true)
    }
    const state = run.value as RunState | null
    if (state) for (const id of taught) if (!state.tutorialSeen.includes(id)) state.tutorialSeen.push(id)
    return true
  }
  /** Peek the daily slot without activating it (title-screen card). */
  function peekDailyRun(): Partial<RunState> | null {
    try {
      const raw = localStorage.getItem(DAILY_STORAGE_KEY)
      return raw ? (JSON.parse(raw) as Partial<RunState>) : null
    } catch {
      return null
    }
  }

  /** Activate today's saved daily (false when absent/stale/finished —
   * the caller starts a fresh one instead). */
  function continueDailyRun(): boolean {
    const raw = peekDailyRun()
    if (!raw || raw.dailyDate !== weeklyDateKey() || raw.defeated || raw.bossDefeated) return false
    run.value = normalizeRun(raw)
    return true
  }

  /** Swap back to the campaign slot (after playing a daily). */
  function activateCampaignRun(): boolean {
    try {
      const raw = localStorage.getItem(RUN_STORAGE_KEY)
      if (!raw) {
        run.value = null
        return false
      }
      const asc = useAscension()
      asc.load()
      run.value = normalizeRun(JSON.parse(raw) as Partial<RunState>, asc.unlocked.value)
      return true
    } catch {
      return false
    }
  }

  /** How many creatures this run drafts (A10 removes one; the Gauntlet
   * keeps A9's three - v6.99, per Javi). */
  function draftRosterSize(state: RunState): number {
    // 🍼 Normal mode drafts FOUR (v1.88, per Javi) — the ascensions
    // keep the tighter three (and A10 its two). The ladder itself lives
    // in ascensions.ts as of v3.83, where a test can walk every rung.
    // 🎒 Loadout Lock week: keep five of the eight.
    if (hasWeeklyMod(state, 'loadout')) return 5
    return ascensionDraftSize(state.ascension, TUNING.draft.rosterSize, !!state.gauntlet) + starweaveDraftExtra(state)
  }

  /**
   * Free-pick draft (v0.61): the whole creature pool is offered; the
   * player chooses their starting team (3, or 2 on Ascension 10).
   */
  function confirmDraft(defIds: string[]) {
    const state = run.value
    if (!state?.draft || state.phase !== 'draft') return
    const unique = [...new Set(defIds)]
    if (unique.length !== draftRosterSize(state)) return
    // Picks come from the rolled nine OR the owned-rares section (v1.01).
    const pickable = new Set([...state.draft.options, ...(state.unlockedCreatures ?? [])])
    if (!unique.every(id => pickable.has(id))) return
    // 🎓 v7.39 (per Javi): funnel - a campaign team is picked.
    if (funnelCampaign(state)) track('draft_confirmed', { once: true })

    state.roster = unique.map(id => makeCreature(getCreatureDef(id)))
    if (state.starweave) state.starweave.firstDraftedCreatureId = state.roster[0]?.id
    // Play, not acquisition — no ✨ New glow for a drafted creature (v3.97).
    for (const id of unique) useCollection().record('creatures', id, { announce: false })
    // A7 Rough Start (v1.75 swap): the team limps out of the gate.
    if (ascensionStartsInjured(state.ascension)) {
      for (const creature of state.roster) injureCreature(creature, state)
    }
    // 🎁 v7.33 (per Javi: "make it as simple as possible to get started"):
    // NORMAL runs skip the starting relic - the team goes straight to the
    // ready screen and the map. The daily/weekly keeps it (the relic trio
    // is part of the seeded skeleton everyone shares) and so does the
    // Gauntlet (its A10 rules lean on the pick). v7.37 (per Javi): only
    // NORMAL mode (ascension 0) skips it - every ascension keeps the pick.
    if (state.mode !== 'daily' && !state.gauntlet && state.ascension === 0) {
      state.draft.bonusOptions = []
      state.phase = 'summary'
      return
    }
    state.phase = 'bonus'
    // The starting bonus is (for now) a starting relic drawn from the
    // same pool as treasure nodes, rarity and all.
    // 🎲 Daily (v5.54): the relic trio is seeded too — rolled whenever
    // the player confirms, but from a stream keyed off the day's seed,
    // so everyone faces the same three.
    if (state.mode === 'daily' && state.seed) {
      setRandomSource(seededRng(`${state.seed}/relics`))
      try {
        state.draft.bonusOptions = generateOffers('relic', state).map(o => o.defId)
        // 🚀 Head Start week: a BONUS relic on top of the pick, seeded
        // like everything else in the skeleton.
        if (hasWeeklyMod(state, 'headstart')) {
          const extra = generateOffers('relic', state)[0]?.defId
          if (extra) grantRelic(state, extra)
        }
      } finally {
        setRandomSource(null)
      }
    } else {
      state.draft.bonusOptions = generateOffers('relic', state).map(o => o.defId)
    }
    // 🚀 Head Start week: the team marches out at level 5 — the XP path
    // (not a stat stamp), so the level-up picks are still yours to spend.
    if (hasWeeklyMod(state, 'headstart')) {
      for (const creature of state.roster) {
        let guard = 0
        while (creature.level < 5 && guard++ < 200) grantXp(creature, 5, state)
      }
    }
  }

  /** The boss-reveal intro plays once, right after Start Run. */
  function dismissBossIntro() {
    if (run.value) run.value.bossIntroSeen = true
  }

  /** Pocket a relic + its on-pickup price. Every acquisition door —
   * starting pick, shop, reward card — funnels through here so a
   * pickup effect can never be dodged by taking the other door. */
  function grantRelic(state: RunState, relicId: string) {
    if (!state.relics.includes(relicId)) state.relics.push(relicId)
    useCollection().master('relics', relicId)
    // 👑 v4.14 (per Javi): taking the crown costs EVERY coin you hold.
    if (relicId === 'relic-paupers-crown') state.gold = 0
  }

  /** The run-defining pick after the draft: 1 of 3 relics. */
  function pickStartingRelic(relicId: string) {
    const state = run.value
    if (!state?.draft || state.phase !== 'bonus') return
    if (!state.draft.bonusOptions.includes(relicId)) return
    if (state.starweave?.collectorPending?.source === 'starting') {
      if (!consumeStarweaveCollector(state, relicId)) return
      grantRelic(state, relicId)
      state.phase = 'summary'
      return
    }
    const offered = state.draft.bonusOptions.map(defId => ({ id: defId, defId, pool: 'relic' as const }))
    recordDeclinedOffers(state, 'relic', state.draft.bonusOptions, relicId)
    grantRelic(state, relicId)
    if (offerStarweaveCollector(state, offered, 'starting')) {
      state.draft.bonusOptions = state.starweave!.collectorPending!.offers.map(o => o.defId)
      return
    }
    state.phase = 'summary'
  }

  const canRerollRelics = computed(() => canStarweaveRelicReroll(run.value))
  function rerollRelics() {
    const state = run.value
    if (!state || !rerollStarweaveRelics(state)) return false
    const ids = state.phase === 'bonus' ? state.draft!.bonusOptions : (state.pendingRewards ?? []).map(o => o.defId)
    for (const id of ids) useCollection().record('relics', id, { announce: false })
    return true
  }

  /** 🎓 v6.85 Broader Curriculum: may this class take a second lesson? */
  function canDoubleTraining(missionId: string) { return canStarweaveDoubleTraining(run.value, missionId) }

  function canRerollTraining(missionId: string) {
    return canStarweaveSkillReroll(run.value, missionId)
  }
  function rerollTraining(missionId: string) {
    const state = run.value
    if (!state || !rerollStarweaveTraining(state, missionId)) return false
    const mission = state.encounter!.missions.find(m => m.id === missionId)!
    for (const id of mission.trainingOptions ?? []) useCollection().record('skills', id, { announce: false })
    return true
  }

  function declineExtraRelic() {
    const state = run.value
    const pending = state?.starweave?.collectorPending
    if (!state || !pending) return
    recordDeclinedOffers(state, 'relic', pending.offers.map(o => o.defId))
    delete state.starweave!.collectorPending
    if (pending.source === 'starting') state.phase = 'summary'
    else {
      state.pendingRewards = null
      advanceRewardStage()
    }
  }

  /** 🙅 v7.78 (per Javi: "make the chests have an option to bypass relic reward"): every in-run relic pick - a chest,
   * mystery treasure, an elite's or act boss's relic stage, an earned puzzle relic - can be walked away from. Nothing
   * is granted and nothing is paid back. The shown relics join the decline cycle like any walked-away screen (the
   * v5.17 rule); the Master Collector's act use is untouched (it only follows a real grant), and its extra pick keeps
   * its own Skip. The starting pick stays a pick. */
  function declineRelicOffer() {
    const state = run.value
    const offers = state?.pendingRewards
    if (!state || !offers?.length || state.rewardResolution || state.starweave?.collectorPending) return
    if (!offers.every(o => o.pool === 'relic')) return
    recordDeclinedOffers(state, 'relic', offers.map(o => o.defId))
    state.pendingRewards = null
    advanceRewardStage()
  }

  function continueToMap() {
    const state = run.value
    if (state?.phase !== 'summary') return
    state.phase = 'map'
    state.draft = null
  }

  /** Backfills a map for saves created before the map system existed. */
  function ensureMap() {
    const state = run.value
    if (!state) return
    if ((state.phase === 'map' || state.phase === 'encounter') && !state.map) {
      state.map = generateMap(state.ascension ?? 0)
    }
  }

  function getNode(nodeId: string | null): MapNode | null {
    if (!nodeId) return null
    return run.value?.map?.nodes.find(n => n.id === nodeId) ?? null
  }

  /** Nodes the player can enter right now. */
  function availableNodeIds(): string[] {
    const state = run.value
    if (!state?.map || state.phase !== 'map' || state.bossDefeated) return []
    if (!state.position) return state.map.nodes.filter(n => n.row === 0).map(n => n.id)
    return getNode(state.position)?.next ?? []
  }

  function confirmNode(nodeId: string) {
    const state = run.value
    if (!state || !availableNodeIds().includes(nodeId)) return
    // 🎓 v7.39 (per Javi): funnel - 'Take this path' commits here (both of map.vue's doors).
    if (funnelCampaign(state)) track('map_path_taken', { once: true })
    state.activeNodeId = nodeId
    state.phase = 'encounter'
    const node = getNode(nodeId)
    // 🦴 v5.57 (per Javi): everything a node reveals ON ENTRY — a
    // mystery's true face and its event, a shop's shelves, a rest or
    // chest's cards — is skeleton in a daily, keyed per node so it is
    // path-independent. Campaign runs: pass-through, live dice.
    withRunStream(state, `node/${nodeId}`, () => {
    // Mystery nodes reveal what they really are on entry.
    if (node?.type === 'mystery') {
      const { ambush, treasure, respite, puzzle, goodEvent } = TUNING.map.mysteryOutcomes
      const roll = rand()
      if (roll < ambush) {
        node.type = 'ambush'
        const [min, max] = TUNING.map.requiredSuccesses.ambush
        node.preview = {
          requiredSuccesses: randInt(min, max) + goalBonusForAct(state.act, 'ambush'),
          focusElements: sample(ELEMENTS, 2),
          tier: tierForRow('ambush', node.row % (state.map?.rowsPerAct ?? TUNING.map.rows)),
        }
      } else if (roll < ambush + treasure) {
        state.pendingRewardPools = ['relic']
        advanceRewardStage()
      } else if (roll < ambush + treasure + respite) {
        starweaveRations(state, 'rest')
        state.pendingRewardPools = ['healing']
        advanceRewardStage()
      } else if (roll < ambush + treasure + respite + puzzle) {
        // A puzzle in disguise (v0.59).
        node.type = 'puzzle'
        node.preview = {
          requiredSuccesses: 3,
          focusElements: [],
          tier: tierForRow('puzzle', node.row % (state.map?.rowsPerAct ?? TUNING.map.rows)),
        }
      } else {
        // ❓ v5.39: a narrative EVENT (designed with Javi) — drawn from
        // the good or bad deck, resolved by player choice on arrival.
        const group = roll < ambush + treasure + respite + puzzle + goodEvent ? 'good' : 'bad'
        state.pendingEvent = rollEvent(state, group)
        // 📜 v6.52: the collector's ledger (every event ever dealt).
        lifetime.recordEvent(state.pendingEvent)
      }
      return
    }
    // The shop rolls its stock on entry: 6 random relics, priced by rarity.
    if (node?.type === 'shop') {
      const candidates = RELIC_POOL
        .filter(r => relicAvailable(r, state.ascension))
        .filter(r => !state.relics.includes(r.id))
      // 🧩 Synergy nudge (v2.38, per Javi): the stock leans lightly
      // toward relics that pair with the run's build (data/synergies.ts).
      const owned = ownedSynergyIds(state)
      const weighted = candidates.map(r => [r, synergyWeightMult(r.id, owned)] as const)
      const stock: typeof candidates = []
      while (stock.length < Math.min(TUNING.shop.stock * gildedRestockMult(state.roster), candidates.length) && weighted.length > 0) {
        const pick = weightedPick(weighted)
        weighted.splice(weighted.findIndex(([r]) => r === pick), 1)
        stock.push(pick)
      }
      state.shopStock = stock.map(r => ({ relicId: r.id, price: TUNING.shop.prices[rarityOf(r.id)] }))
      // 👁️ v6.03: the shelf on display is a sighting.
      for (const r of stock) useCollection().record('relics', r.id, { announce: false })
      return
    }
    // Non-combat nodes go straight to their reward choice.
    if (node?.type === 'rest' || node?.type === 'chest') {
      // 🌿 v4.66 (per Javi): a Green Thumb roster grows max HP the
      // moment it ARRIVES at a rest stop.
      if (node.type === 'rest') {
        greenThumbArrive(state)
        starweaveRations(state, 'rest')
      }
      state.pendingRewardPools = poolsForNode(node.type)
      advanceRewardStage()
    }
    })
  }

  /** Haggler's Charm (v0.76): shop prices are halved, live — applies to
   * stock rolled before the charm was won, too. */
  function shopPrice(state: RunState, base: number): number {
    const haggled = state.relics.includes('relic-haggler-charm') ? base / 2 : base
    // 🐍 Gilded Scales (v2.87): stacks multiplicatively with Haggler's
    // Charm, so the two are worth owning together.
    return Math.max(1, Math.ceil(haggled * (1 - gildedScalesDiscount(state.roster))))
  }

  /** Shop purchase: pay gold, pocket the relic, thin the stock. */
  function buyRelic(relicId: string) {
    const state = run.value
    const item = state?.shopStock?.find(s => s.relicId === relicId)
    if (!state || !item) return
    const price = shopPrice(state, item.price)
    if (state.gold < price) return
    state.gold -= price
    state.goldSpent = (state.goldSpent ?? 0) + price // 🧘 v6.54
    grantRelic(state, relicId)
    starweaveShopCashback(state, price)
    state.shopStock = state.shopStock!.filter(s => s.relicId !== relicId)
  }

  /** Pops the next reward pool into an active 3-card offer, if any. */
  function advanceRewardStage() {
    const state = run.value
    if (!state || state.starweave?.collectorPending) return
    const pool = state.pendingRewardPools.shift()
    const node = getNode(state.activeNodeId)
    // 🦴 v5.57: daily reward cards are skeleton, keyed node+pool so an
    // elite/boss's second stage draws its own stream.
    state.pendingRewards = pool
      ? withRunStream(state, `offers/${node?.id ?? 'x'}/${pool}`, () =>
          generateOffers(pool, state, rarityFloor(node?.type, pool), node?.type))
      : null
    // 👁️ v6.03: dealt cards are sightings - relic offers light the
    // relic shelf, everything else the rewards shelf.
    for (const offer of state.pendingRewards ?? []) {
      useCollection().record(offer.pool === 'relic' ? 'relics' : 'rewards', offer.defId, { announce: false })
    }
  }

  function completeActiveNode() {
    const state = run.value
    const node = getNode(state?.activeNodeId ?? null)
    if (!state || !node || state.phase !== 'encounter' || state.starweave?.collectorPending) return
    // 🏆 v6.03: surviving an elite's law masters its codex entry.
    if (node.modifier) useCollection().master('emods', node.modifier)
    if (node.modifier2) useCollection().master('emods', node.modifier2)
    // Fresh node, fresh surges: cooldowns and study clocks reset between
    // nodes. 💨/⚓ stacks PERSIST across nodes since v1.56 (per Javi) —
    // only the creature's own failure drops them (useEncounter).
    for (const creature of state.roster) {
      creature.surgeCooldown = 0
      creature.deepRootsClock = 0
    }
    // Ascension 1–2 injuries end with the node; A3+ scars persist.
    if (nodeEndClearsInjuries(state)) {
      for (const creature of state.roster) healInjuriesFully(creature)
    }
    // Collection: any passive awakened by now is logged for the codex
    // (🌗 Second Calling's bonus passive counts too).
    for (const creature of state.roster) {
      for (const passive of creaturePassives(creature)) {
        useCollection().record('passives', passive.def.id)
      }
    }
    state.completedNodeIds.push(node.id)
    // 🎓 v7.40 (per Javi, §3 D): location 1 cleared - the first run's rails
    // end, and this device's first run is done for good.
    if (state.firstRun) {
      state.firstRun = false
      writeFirstSteps(typeof localStorage === 'undefined' ? null : localStorage, { firstRun: 'done' })
    }
    state.position = node.id
    state.activeNodeId = null
    state.encounter = null
    state.pendingRewards = null
    state.pendingRewardPools = []
    state.rewardResolution = null
    state.pendingEvent = null
    delete state.pendingEventResolved
    state.shopStock = null
    if (node.type === 'boss') {
      const rowsPerAct = state.map?.rowsPerAct ?? TUNING.map.rows
      const bossAct = Math.floor(node.row / rowsPerAct) + 1
      // 🌟 Stardust (v0.76): completing an act pays global currency —
      // deeper acts pay more, higher ascensions pay more (v0.90).
      // 🏦 v4.92: reported as an EVENT — the wallet server computes the
      // amount itself and pays each (runId, act) exactly once.
      if (!state.runId) state.runId = newRunId()
      // 🎲 Daily runs (v5.54) earn NO in-run stardust — unlimited
      // attempts at a fresh runId each would mint act-clears forever.
      // The daily's prize is the board (top 3 pay via server claim).
      if (state.mode !== 'daily') {
        useStardust().earnEvent({
          kind: 'act-clear', runId: state.runId!, act: bossAct, ascension: state.ascension ?? 0,
        })
      }
      // 🏆 v6.03: the fallen boss's codex entry is MASTERED.
      if (node.preview?.boss?.skill) useCollection().master('bosses', node.preview.boss.skill)
      // 🏆 v6.52 marks, read BEFORE the act heal: a boss felled on the
      // last point of health, and (v6.53: at Ascension 8+, per Javi) an
      // act with no mission failed or expired.
      if (typeof state.puzzleLevel !== 'number') {
        if (state.hp <= 1) lifetime.mark('brinkBoss', 1)
        const log = state.actLog[bossAct]
        if (log && log.failures === 0 && log.expiries === 0 && (state.ascension ?? 0) >= 8) lifetime.mark('untouchedAct', 1)
      }
      // A boss down deserves a breather: the act summary screen.
      state.actSummary = bossAct
      if (bossAct < TUNING.acts.count) {
        // Act boss down: a FULL heal (v0.79) — capped at 80% of max on
        // Ascension 4+ (v0.80); never lowers HP you already have.
        onActComplete(state)
        state.act = bossAct + 1
        state.hp = Math.max(state.hp, Math.round(state.maxHp * ascensionActHealPct(state.ascension ?? 0)))
        starweaveRations(state, 'act')
      } else if (state.gauntlet) {
        // ⚔️ Gauntlet (v2.34): there is NO summit (the A10 secret boss
        // never fires — its skill joins the act 4+ cycle instead).
        // v3.37 (per Javi): NO act heal past act 3 — the gauntlet is a
        // war of attrition, and a free top-up every act undid it. Rest
        // stops and healing rewards are the only medicine now.
        onActComplete(state)
        state.act = bossAct + 1
        appendGauntletAct(state.map!, state.act, state.ascension ?? 0)
        starweaveRations(state, 'act')
      } else if (ascensionSecretBoss(state.ascension ?? 0) && !state.secretBossRevealed) {
        // 🤫 A10 (v0.84): the run is NOT over. A hidden FOURTH boss
        // rises one row past the summit — no act advance, no heal.
        state.secretBossRevealed = true
        const secret = makeSecretBossNode(state.map!, state.ascension ?? 0)
        state.map!.nodes.push(secret)
        node.next.push(secret.id)
        state.map!.rows = secret.row + 1
      } else {
        state.bossDefeated = true
        // 📊 v5.64: a finished expedition banks its lifetime win.
        const done = new Set(state.completedNodeIds)
        // 🎗️🧘 v6.54: the homage chains, read at the summit. A10 cleared
        // is checked HERE, at the moment of the win (a win before it
        // never counts later - the chains are for after the climb).
        const ledger = useAscension()
        ledger.load()
        const learned = new Set(state.roster.flatMap(c => c.learnedSkills ?? []))
        const hasKeepsake = state.relics.includes(KEEPSAKE_RELIC)
        const hasLesson = learned.has(KEEPSAKE_SKILL)
        lifetime.recordRunEnd(statsModeOf(state), 'won', state.playtimeMs ?? 0, state.ascension ?? 0, {
          act: state.act,
          legacyOnRoster: state.roster.some(c => c.defId === 'legacy'),
          // 🏆 v6.52: not one expiry all run; no elite or titan ever fought.
          noExpiry: Object.values(state.actLog).every(l => (l.expiries ?? 0) === 0),
          noElite: !state.map!.nodes.some(n => done.has(n.id) && (n.type === 'elite' || n.type === 'titan')),
          a10Cleared: ledger.cleared.value.includes(10),
          keepsakeOnly: hasKeepsake && state.relics.length === 1,
          lessonOnly: hasLesson && learned.size === 1,
          keepsakeAndLesson: hasKeepsake && hasLesson,
          ascetic: !(state.surgesFired ?? 0) && !(state.skillsUsed ?? 0) && !(state.recruits ?? 0) && !(state.goldSpent ?? 0),
        })
        // 🎲 Daily (v5.54): a win is a board entry, nothing more — no
        // ascension unlocks (the seeded A2-9 wasn't earned) and no
        // first-clear bounties.
        if (state.mode !== 'daily') {
          // Winning on ascension N unlocks N+1 (meta progress, not run state).
          const ascension = state.ascension ?? 0
          const asc = useAscension()
          // 🔓 v7.79 (per Javi: "you dont have to anounce what ascension was unlocked if the player already has it
          // unlocked"): the victory banner names an unlock only when this win made one.
          asc.load()
          const unlockedBefore = asc.unlocked.value
          asc.recordWin(ascension)
          if (asc.unlocked.value > unlockedBefore) state.newAscension = asc.unlocked.value
          // 🏅 First-clear bounty (v3.62, per Javi; halved v5.16): 50 ⭐
          // per ascension level, once per level ever. Normal and A1 both
          // pay 50 (there is no level 0 payout), A2 pays 100, up to A10's
          // 500. The Gauntlet never reaches here — it has no summit.
          if (asc.markCleared(ascension)) {
            state.firstClearBounty = firstClearStardust(ascension)
            // 🏅 v4.15 gated this through /api/bonus/claim; 🏦 v4.92 the
            // wallet earn route checks-and-marks the SAME bonusClaims doc
            // atomically with the credit, so a bounty claimed under either
            // system can never pay twice.
            useStardust().earnEvent({ kind: 'ascension-first', ascension })
          }
        }
      }
    }
    // Node-only Speed/Fight ends when the player actually leaves, including
    // after a shop or event. Reward/act XP finishes before this reset.
    if (state.starweave) delete state.starweave.node
    state.phase = 'map'
  }

  function dismissActSummary() {
    if (run.value) run.value.actSummary = null
  }

  /**
   * Applies the chosen reward card, stages its follow-up selection, or —
   * once fully resolved — advances to the next reward stage (elites and
   * act bosses queue a relic stage after the regular one).
   */
  function chooseReward(offerId: string) {
    const state = run.value
    const chosen = state?.pendingRewards?.find(o => o.id === offerId)
    if (!state || !chosen) return
    if (state.starweave?.collectorPending?.source === 'reward') {
      if (!consumeStarweaveCollector(state, chosen.defId)) return
      grantRelic(state, chosen.defId)
      state.pendingRewards = null
      advanceRewardStage()
      return
    }
    const offered = [...(state.pendingRewards ?? [])]
    // 🔁 v5.17 (per Javi): relics left on the table enter the decline
    // cycle — they sit out future offers until the pool comes around.
    if (chosen.pool === 'relic') {
      recordDeclinedOffers(state, 'relic',
        (state.pendingRewards ?? []).filter(o => o.pool === 'relic').map(o => o.defId), chosen.defId)
    }
    state.pendingRewards = null

    if (chosen.pool === 'relic') {
      grantRelic(state, chosen.defId)
      if (offerStarweaveCollector(state, offered, 'reward')) {
        state.pendingRewards = state.starweave!.collectorPending!.offers.map(o => ({ ...o }))
        return
      }
      advanceRewardStage()
      return
    }
    useCollection().master('rewards', chosen.defId)
    // 🦴 v5.57: what a picked card ROLLS (recruit options, tie-breaks)
    // is skeleton too — the skills card already deals from the deck.
    const resolution = withRunStream(state, `reward/${state.activeNodeId ?? 'x'}/${chosen.defId}`, () =>
      getRewardDef(chosen.defId).apply(state))
    // 🌱 v4.85 (per Javi: "Field Promotion with 5 creatures should have
    // leveled him twice"): rewards land AFTER onNodeComplete drained the
    // Evergrowth queue — absorb again so a Rootwarden converts reward
    // level-ups NOW, not mid-next-node (or never, on the last node).
    evergrowthAbsorb(state.roster, state)
    if (resolution) state.rewardResolution = resolution
    else advanceRewardStage()
  }

  /** Pick-creature rewards (Private Lesson, Team Drill): grant XP each. */
  function resolveRewardCreatures(creatureIds: string[]) {
    const state = run.value
    const resolution = state?.rewardResolution
    if (!state || resolution?.type !== 'pick-creatures') return
    const picked = state.roster.filter(c => creatureIds.includes(c.id)).slice(0, resolution.count)
    if (picked.length !== resolution.count) return
    for (const creature of picked) grantXp(creature, resolution.xpEach, state)
    // 🌱 v4.85: same late-queue drain as the reward apply above.
    evergrowthAbsorb(state.roster, state)
    state.rewardResolution = null
    advanceRewardStage()
  }

  /**
   * Recruit reward: the option's stats were pre-rolled when offered, so
   * the chosen creature joins with exactly the spread shown on the card.
   */
  function resolveRecruitReward(defId: string) {
    const state = run.value
    const resolution = state?.rewardResolution
    if (!state || resolution?.type !== 'recruit') return
    const option = resolution.options.find(o => o.defId === defId)
    if (!option) return
    // 👁️ v6.03: every face on the recruit card was seen.
    for (const o of resolution.options) useCollection().record('creatures', o.defId, { announce: false })
    // 🔁 v5.17: the recruits not taken wait out the cycle.
    recordDeclinedOffers(state, 'recruit', resolution.options.map(o => o.defId), defId)
    const recruit = makeCreature(getCreatureDef(defId))
    recruit.level = resolution.level
    recruit.stats = { ...option.stats }
    state.roster.push(recruit)
    starweaveRecruitCatchUp(state, recruit)
    evergrowthAbsorb(state.roster, state)
    state.recruits = (state.recruits ?? 0) + 1 // 🧘 v6.54
    // A recruit joins the RUN, not the collection shelf — quiet (v3.97).
    useCollection().record('creatures', defId, { announce: false })
    state.rewardResolution = null
    advanceRewardStage()
  }

  /** 🙅 v4.37 (per Javi): a recruit offer or a training can be DECLINED
   * outright — the pick clears and the reward flow moves on. The shop
   * special's 120 gold is forfeit on decline, not refunded: declining
   * is walking away, not a return counter. */
  function declineResolution() {
    const state = run.value
    if (!state?.rewardResolution) return
    // 🔁 v5.17 (per Javi): walking away declines EVERYTHING shown —
    // the whole screen enters the cycle, chosen-but-unconfirmed included.
    const res = state.rewardResolution
    if (res.type === 'recruit') recordDeclinedOffers(state, 'recruit', res.options.map(o => o.defId))
    else if (res.type === 'train') recordDeclinedOffers(state, 'skill', res.options)
    state.rewardResolution = null
    advanceRewardStage()
  }

  /** 🎓 Training Ground step 1 (v0.84): lock in the skill to teach. */
  function resolveTrainSkill(skillId: string) {
    const state = run.value
    const resolution = state?.rewardResolution
    if (!state || resolution?.type !== 'train' || resolution.chosen) return
    if (!resolution.options.includes(skillId)) return
    resolution.chosen = skillId
  }

  /** 🎓 Training Ground step 2: the student learns it instantly. */
  function resolveTrainee(creatureId: string) {
    const state = run.value
    const resolution = state?.rewardResolution
    if (!state || resolution?.type !== 'train' || !resolution.chosen) return
    const creature = state.roster.find(c => c.id === creatureId)
    if (!creature || (creature.learnedSkills ?? []).includes(resolution.chosen)) return
    // 🔁 v5.17: the skills not chosen wait out the cycle.
    recordDeclinedOffers(state, 'skill', resolution.options, resolution.chosen)
    creature.learnedSkills = [...(creature.learnedSkills ?? []), resolution.chosen]
    // 🎓 One School week: the lesson carries to the WHOLE team (team-wide
    // effects still apply once per team — the existing no-stack rules).
    if (hasWeeklyMod(state, 'oneschool')) {
      for (const mate of state.roster) {
        if (mate.id === creature.id || !canLearnSkill(mate, resolution.chosen)) continue
        mate.learnedSkills = [...(mate.learnedSkills ?? []), resolution.chosen]
      }
    }
    useCollection().master('skills', resolution.chosen)
    state.rewardResolution = null
    advanceRewardStage()
  }

  /**
   * Shop specials (v0.84, per Javi): every shop ALWAYS offers a
   * 🎓 Training Ground and a 🐾 New Companion for a flat 120 gold
   * (Haggler's Charm halves it like everything else). The purchase
   * drops into the same follow-up picks as the reward cards; the shop
   * returns once the pick resolves.
   */
  function buyShopSpecial(kind: 'training' | 'companion') {
    const state = run.value
    if (!state || state.rewardResolution) return
    const defId = kind === 'companion' ? 'new-companion' : 'training-ground'
    const def = getRewardDef(defId)
    if (def.canOffer && !def.canOffer(state)) return
    const price = shopPrice(state, TUNING.shop.specialPrice)
    if (state.gold < price) return
    state.gold -= price
    state.goldSpent = (state.goldSpent ?? 0) + price // 🧘 v6.54
    useCollection().master('rewards', defId)
    // 🦴 v5.57: the shop specials' rolls join the skeleton like any
    // picked card (keyed to the shop node the player is standing in).
    state.rewardResolution = withRunStream(state, `reward/${state.activeNodeId ?? 'x'}/${defId}`, () =>
      def.apply(state)) ?? null
    // 🌱 v4.85: shop specials grant XP outside resolutions too.
    evergrowthAbsorb(state.roster, state)
  }

  function abandonRun() {
    if (restorePracticeRun()) return
    if (run.value?.mode === 'tutorial') useOnboarding().setPreference('dismissed')
    // 📊 v5.64: only an UNFINISHED run counts as abandoned — leaving a
    // victory or defeat screen through here is already settled.
    settleAsAbandoned(run.value)
    run.value = null
  }

  /** 🔒 v6.83 ("bound", per Javi): true while a campaign or gauntlet run
   * that set out with Starweave stars is still in progress on this device —
   * on screen, or stashed behind a daily. The modal keeps Unbind shut for it;
   * the server asks the cloud save the same question. */
  function starweaveBoundRun(): boolean {
    const active = run.value
    if (active?.mode === 'tutorial' && active.tutorialSeen.includes('guide:stash')) { // 🎓 v7.40: only a stashing practice holds one
      try {
        const saved = JSON.parse(localStorage.getItem(PRACTICE_STASH_KEY) ?? 'null')
        return runBindsStarweave(saved?.run) || runBindsStarweave(saved?.campaignRaw ? JSON.parse(saved.campaignRaw) : null)
      } catch { return true }
    }
    if (active && active.mode !== 'daily') return runBindsStarweave(active)
    try { return runBindsStarweave(JSON.parse(localStorage.getItem(RUN_STORAGE_KEY) ?? 'null')) } catch { return false }
  }

  /** Freehand route planning strokes (map page). */
  function addPlannedStroke(points: { x: number; y: number }[]) {
    if (run.value && points.length > 1) run.value.plannedPath.push({ points })
  }

  function clearPlannedPath() {
    if (run.value) run.value.plannedPath = []
  }

  /** 🚫 v5.42 Grueling (elite modifier): on-use effects are dead for
   * the whole node — nothing new gets armed or fired here. Disarming
   * stays allowed, so nothing can be left stuck primed. */
  function gruelingHere(state: RunState | null | undefined): boolean {
    if (!state) return false
    const node = getNode(state.activeNodeId)
    // 👿 v5.66 Twin Curses: either slot can carry the rule.
    return node?.modifier === 'grueling' || node?.modifier2 === 'grueling'
  }

  /** 🚫 v6.00 (per Javi: "make the on-use skills grayed out"): the
   * component-facing read of the Grueling rule. */
  const gruelingActive = computed(() => gruelingHere(run.value))

  /** Arms (or disarms) a creature's innate surge for its next mission. */
  function toggleSurge(creatureId: string) {
    const state = run.value
    const creature = state?.roster.find(c => c.id === creatureId)
    if (!creature || !creature.available) return
    if (creature.surgePrimed) {
      // 📣 v7.62 (per Javi): a Rallying Cry holder's Surge is always armed - no disarming.
      if (creature.learnedSkills?.includes('rallying-cry')) return
      creature.surgePrimed = false
      return
    }
    if (gruelingHere(state)) return
    if (creature.surgeCooldown > 0) return
    creature.surgePrimed = true
    breakSilence(state) // 🕊️ v6.90: arming a surge is a press
  }


  // 🎯 Creature on-use skills (v2.86). `graftSource` is the pick-a-
  // teammate mode: clicking Graft parks the caster here, and the next
  // roster tap completes it. Kept in run-level state so the roster bar
  // and the assignment window drive the same interaction.
  const graftSource = useState<string | null>('graft-source', () => null)
  /** ⚒️ Reforge (v3.02): target picked, modal open, allocation pending. */
  const reforgePicker = useState<{ smithId: string; targetId: string } | null>('reforge-picker', () => null)
  /**
   * 👑 Decree (v3.06): armed — the next eligible Fight is a decree.
   * v3.56: backed by the RUN STATE rather than a `useState`, so refreshing
   * the page (or the PWA reloading itself) can no longer drop an order the
   * player has already given.
   */
  const decreeArmed = computed<string | null>({
    get: () => run.value?.roster.find(c => c.decreeArmed)?.id ?? null,
    set: (id) => {
      for (const creature of run.value?.roster ?? []) {
        creature.decreeArmed = creature.id === id ? true : undefined
      }
    },
  })

  /**
   * Fires (or arms) a creature's own skill. Instant skills that need a
   * target enter targeting mode instead and resolve in completeGraft.
   * Returns what happened, so the UI can pick a sound.
   */
  /** 🧘 v6.54: what The Ascetic counts as a creature skill USED - armed
   * (a primed kind) or fired outright; a targeted one when it lands. */
  function noteSkillUsed(state: RunState) {
    state.skillsUsed = (state.skillsUsed ?? 0) + 1
  }
  function useCreatureSkill(creatureId: string): 'armed' | 'disarmed' | 'fired' | 'targeting' | 'none' {
    const result = useCreatureSkillInner(creatureId)
    if ((result === 'armed' || result === 'fired') && run.value) noteSkillUsed(run.value)
    // 🕊️ v6.90: a press is a press - except Let Go, which spends the Silence itself.
    if ((result === 'armed' || result === 'fired') && run.value) {
      const pressed = run.value.roster.find(c => c.id === creatureId)
      if (!pressed || creatureOwnSkill(pressed)?.id !== 'let-go') breakSilence(run.value)
    }
    return result
  }
  function useCreatureSkillInner(creatureId: string): 'armed' | 'disarmed' | 'fired' | 'targeting' | 'none' {
    const state = run.value
    const creature = state?.roster.find(c => c.id === creatureId)
    if (!state || !creature) return 'none'
    const def = creatureOwnSkill(creature)
    if (!def) return 'none'
    if (def.kind === 'primed') {
      const wasPrimed = creature.skillPrimed
      // 🎲 v7.79: a placed bet (he walked out on a stamped mission) cannot be called off - that dodged the cooldown
      // while the stamp kept the bet riding. Planned into a trial or a boss piece, he can still change his mind.
      if (wasPrimed && wagerPlaced(creature, state)) return 'none'
      // 🚫 Grueling: disarming allowed, arming is not.
      if (!wasPrimed && gruelingHere(state)) return 'none'
      if (!toggleCreatureSkill(creature)) return 'none'
      return wasPrimed ? 'disarmed' : 'armed'
    }
    // 🚫 Grueling blocks every instant/targeted fire; only the decree
    // DISARM below stays reachable.
    if (gruelingHere(state) && !(def.id === 'decree' && decreeArmed.value === creatureId)) return 'none'
    if (!canUseCreatureSkill(creature)) return 'none'
    if (def.needsTarget) {
      // Second tap on the caster cancels targeting.
      graftSource.value = graftSource.value === creatureId ? null : creatureId
      return graftSource.value ? 'targeting' : 'disarmed'
    }
    if (def.id === 'balance-of-ages') return useBalanceOfAges(creature) ? 'fired' : 'none'
    if (def.id === 'bloom') {
      useBloom(creature, state)
      return 'fired'
    }
    // 🚃 v6.89 Aid: nothing out in the field, nothing to hurry (and nothing spent).
    if (def.id === 'the-carriage') return useCarriage(creature, state) > 0 ? 'fired' : 'none'
    // 🕊️ v6.90 The Noble One: nothing banked, nothing to let go of.
    if (def.id === 'let-go') return useLetGo(creature, state) > 0 ? 'fired' : 'none'
    // 🎯 v6.92 Robodude: only from his station, only with a live mission to shell.
    if (def.id === 'fire-support') return useFireSupport(creature, state) ? 'fired' : 'none'
    if (def.id === 'tithe') return useTithe(creature, state) ? 'fired' : 'none'
    if (def.id === 'bulwark-drop') return useBulwark(creature, state) ? 'fired' : 'none'
    if (def.id === 'plunder') return usePlunder(creature, state) ? 'fired' : 'none'
    if (def.id === 'deluge') return useDeluge(creature, state) ? 'fired' : 'none'
    // 🔭 Foresight joined the targeted flow in v4.66 (per Javi: "change
    // a node to match 1 creature") — routed via completeCreatureSkill.
    if (def.id === 'decree') {
      if (decreeArmed.value === creatureId) { decreeArmed.value = null; return 'disarmed' }
      if (!canDecree(creature)) return 'none'
      decreeArmed.value = creatureId
      return 'targeting'
    }
    return 'none'
  }

  /** Completes a targeted skill on `targetId`. */
  function completeCreatureSkill(targetId: string): boolean {
    const ok = completeCreatureSkillInner(targetId)
    if (ok && run.value) breakSilence(run.value) // 🕊️ v6.90: a landed target is a press
    // 🧘 v6.54: counted when it LANDS - the Reforge modal opening is
    // not a use; its confirm below is.
    if (ok && run.value && !reforgePicker.value) noteSkillUsed(run.value)
    return ok
  }
  function completeCreatureSkillInner(targetId: string): boolean {
    const state = run.value
    const sourceId = graftSource.value
    if (!state || !sourceId) return false
    const source = state.roster.find(c => c.id === sourceId)
    const target = state.roster.find(c => c.id === targetId)
    graftSource.value = null
    if (!source || !target) return false
    // 🔷 Refract shares Graft's pick-a-teammate flow (v2.92). v4.10:
    // run rides along so the snapshot is the target's EFFECTIVE line.
    const def = creatureOwnSkill(source)
    if (def?.id === 'refract') return useRefract(source, target, state)
    // 🎭 Mimic joined the targeted flow in v4.27 (per Javi's review).
    if (def?.id === 'mimic') return useMimic(source, state, target)
    // 🔭 Foresight (v4.66): the field rerolls to match the chosen one.
    if (def?.id === 'foresight') return useForesight(source, state, target)
    // ⚒️ Reforge (v3.02): the target opens the allocation modal instead
    // of resolving immediately — nothing is spent until Confirm.
    if (def?.id === 'reforge') {
      if (source.id === target.id) return false
      reforgePicker.value = { smithId: source.id, targetId: target.id }
      return true
    }
    return useGraft(source, target)
  }

  /** ⚒️ Confirm the modal's allocation (false = invalid, nothing spent). */
  function finalizeReforge(newStats: Stats): boolean {
    const state = run.value
    const pick = reforgePicker.value
    if (!state || !pick) return false
    const smith = state.roster.find(c => c.id === pick.smithId)
    const target = state.roster.find(c => c.id === pick.targetId)
    if (!smith || !target) { reforgePicker.value = null; return false }
    const ok = useReforge(smith, target, newStats)
    if (ok) {
      reforgePicker.value = null
      noteSkillUsed(state) // 🧘 v6.54
    }
    return ok
  }

  function cancelReforge() {
    reforgePicker.value = null
  }

  function cancelCreatureSkill() {
    graftSource.value = null
  }

  /** 🧘 Equilibrium (reworked v1.12, per Javi — was a one-way rewrite):
   * TOGGLES between the creature's trained build and an even spread
   * across all five elements, on a 30s cooldown. The allocation itself
   * is never touched (effectiveStats presents the spread), so flipping
   * back always lands exactly on the original build. */
  function equalizeStats(creatureId: string) {
    const state = run.value
    const creature = state?.roster.find(c => c.id === creatureId)
    if (!creature || !creature.available) return
    if (!(creature.learnedSkills ?? []).includes('equilibrium')) return
    if ((creature.equalizeCooldown ?? 0) > 0) return
    // 🚫 v5.42 Grueling: entering the stance is an on-use effect;
    // leaving it stays allowed.
    if (!creature.equalized && gruelingHere(state)) return
    creature.equalized = !creature.equalized
    creature.equalizeCooldown = TUNING.skills.trainedCooldownSeconds
  }

  /** Arms/disarms an activated trained skill (📖 Tough Learner /
   * 🔥 Pain Into Power): the arm rides the creature's NEXT mission.
   * Re-arming after a consumed charge waits out a 30s cooldown
   * (v1.12, per Javi — same cadence as surges and Equilibrium). */
  function toggleArmedSkill(creatureId: string, skillId: string) {
    const state = run.value
    const creature = state?.roster.find(c => c.id === creatureId)
    if (!creature || !creature.available) return
    if (!(creature.learnedSkills ?? []).includes(skillId)) return
    const armed = creature.armedSkills ?? []
    // 🚫 Grueling: disarming allowed, arming is not.
    if (!armed.includes(skillId) && gruelingHere(state)) return
    if (!armed.includes(skillId) && (creature.armedSkillCooldowns?.[skillId] ?? 0) > 0) return
    creature.armedSkills = armed.includes(skillId)
      ? armed.filter(id => id !== skillId)
      : [...armed, skillId]
  }

  return {
    run,
    startNewRun,
    startDailyRun,
    peekDailyRun,
    continueDailyRun,
    activateCampaignRun,
    confirmDraft,
    toggleSurge,
    graftSource,
    useCreatureSkill,
    completeCreatureSkill,
    cancelCreatureSkill,
    reforgePicker,
    decreeArmed,
    finalizeReforge,
    cancelReforge,
    toggleArmedSkill,
    gruelingActive,
    equalizeStats,
    pickStartingRelic,
    canRerollRelics,
    rerollRelics,
    canRerollTraining,
    rerollTraining,
    canDoubleTraining,
    declineExtraRelic,
    declineRelicOffer,
    continueToMap,
    ensureMap,
    getNode,
    availableNodeIds,
    confirmNode,
    completeActiveNode,
    advanceRewardStage,
    draftRosterSize,
    startFirstDispatch,
    restorePracticeRun,
    finishFirstDispatch,
    retryFirstRun,
    shopPrice,
    chooseReward,
    resolveRewardCreatures,
    resolveRecruitReward,
    resolveTrainSkill,
    resolveTrainee,
    declineResolution,
    buyShopSpecial,
    dismissBossIntro,
    dismissActSummary,
    buyRelic,
    addPlannedStroke,
    clearPlannedPath,
    abandonRun,
    starweaveBoundRun,
  }
}
